import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import anime from 'animejs/lib/anime.es';
import { GatsbyImage } from 'gatsby-plugin-image';
import { getRelAttribute } from '../../../utilities';

import * as style from './ArticleImageBlock.module.scss';

/**
 * Article Image block (custom) with image, author and caption with a link
 */
const ArticleImageBlock = (props) => {
  const {
    imgData,
    mimeType,
    sourceUrl,
    altText,
    hoverTitle,
    authorName,
    websiteName,
    websiteUrl,
    websiteUrlTarget,
    isNoFollow,
    awardsList,
    awardsIndexToDisplay,
    textOverlayClassName,
    setIsOverlayTextAnimating,
    className,
  } = props;
  const isGif = mimeType === 'image/gif';

  const textOverlay = useRef();
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [awardsDisplayedIndex, setAwardsDisplayedIndex] =
    useState(awardsIndexToDisplay);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isOverlayVisible && awardsIndexToDisplay === -1) {
      /**
       * Hide overlay when: overlay is shown and empty space is clicked
       */
      setIsOverlayTextAnimating(true);
      anime({
        targets: textOverlay.current,
        translateY: {
          value: '-100%',
          easing: 'easeInCubic',
          duration: 500,
        },
        complete: () => {
          setIsOverlayVisible(false);
          setAwardsDisplayedIndex(awardsIndexToDisplay);
          setIsOverlayTextAnimating(false);
        },
      });
    } else if (!isOverlayVisible && awardsIndexToDisplay >= 0) {
      /**
       * Show overlay when: overlay is hidden and award is clicked
       */
      setIsOverlayTextAnimating(true);
      setAwardsDisplayedIndex(awardsIndexToDisplay);
      anime({
        targets: textOverlay.current,
        translateY: {
          value: '0%',
          easing: 'easeOutCubic',
          duration: 500,
        },
        complete: () => {
          setIsOverlayVisible(true);
          setIsOverlayTextAnimating(false);
        },
      });
    } else if (isOverlayVisible && awardsIndexToDisplay >= 0) {
      /**
       * Animate contents transition when: overlay is shown and award is clicked
       */
      setAwardsDisplayedIndex(awardsIndexToDisplay);
    }
  }, [awardsIndexToDisplay]);

  return (
    <div className={`${style.imageBlock} ${className}`}>
      <div className={style.imageWrapper}>
        {imgData?.images &&
          (isGif ? (
            <img
              className={style.image}
              style={{ width: '100%' }}
              src={sourceUrl}
              alt={altText}
            />
          ) : (
            <GatsbyImage
              className={style.image}
              image={imgData}
              alt={altText}
              title={hoverTitle}
            />
          ))}
        {awardsList?.length ? (
          <div
            className={`${textOverlayClassName} ${style.overlayTextContainer}`}
            ref={textOverlay}
            style={{ transform: 'translateY(-100%)' }}
          >
            <div className={style.overlayHeading}>
              {awardsList?.[awardsDisplayedIndex]?.awardName}
            </div>
            <div className={style.overlayText}>
              {awardsList?.[awardsDisplayedIndex]?.awardDescriptionPreselection}
            </div>
          </div>
        ) : null}
      </div>
      {authorName && (
        <div className={style.imageCaption}>
          <span>{authorName}</span>
          {websiteName && (
            <>
              <span className={style.separator}> | </span>
              {websiteUrl ? (
                <a
                  className={style.imageAuthorWebsite}
                  href={websiteUrl}
                  title={websiteName}
                  target={websiteUrlTarget}
                  rel={getRelAttribute(websiteUrlTarget, isNoFollow)}
                >
                  {websiteName}
                </a>
              ) : (
                <span className={style.imageAuthorWebsite}>{websiteName}</span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
// TODO: document props
ArticleImageBlock.defaultProps = {
  altText: '',
  authorName: '',
  awardsIndexToDisplay: -1,
  awardsList: [],
  className: '',
  hoverTitle: '',
  isNoFollow: false,
  setIsOverlayTextAnimating: () => {},
  textOverlayClassName: '',
  websiteName: '',
  websiteUrl: '',
  websiteUrlTarget: '_self',
};

ArticleImageBlock.propTypes = {
  /** Image data in gatsby-image format. */
  imgData: PropTypes.any.isRequired,
  /** Image alt text for when image is not displayed (and SEO) */
  altText: PropTypes.string,
  /** Image title to display a tooltip on hover. */
  hoverTitle: PropTypes.string,
  /** Name of a photographer who took the picture. */
  authorName: PropTypes.string,
  /** Name of a website where the picture was published. */
  websiteName: PropTypes.string,
  /** Url of a website where the picture was published. */
  websiteUrl: PropTypes.string,
  /** The target to open a link in (e.g. _blank). */
  websiteUrlTarget: PropTypes.string,
  /** Make a link have ref='nofollow' attribute */
  isNoFollow: PropTypes.bool,
  /** List of awards - objects of the following format: { awardName, awardDescriptionPreselection }. */
  awardsList: PropTypes.array,
  /** Index of an award for it description to be displayed on top of the image. */
  awardsIndexToDisplay: PropTypes.number,
  /** Class name that will be ignored by 'useOnClickOutsideExceptClasses' hook. */
  textOverlayClassName: PropTypes.string,
  /** Function to set 'IsOverlayTextAnimating' to block interactions while animation is in progress. */
  setIsOverlayTextAnimating: PropTypes.func,
  /** Optional class name */
  className: PropTypes.string,
};

export default ArticleImageBlock;
